import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    apiGetInvite,
    apiAcceptInvite,
    formatApiError,
} from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Moon, AlertCircle, CheckCircle2 } from "lucide-react";

export default function AcceptInvite() {
    const { token } = useParams();
    const navigate = useNavigate();
    const { refresh } = useAuth();
    const [invite, setInvite] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState("");
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const data = await apiGetInvite(token);
                setInvite(data);
            } catch (err) {
                setLoadError(formatApiError(err));
            } finally {
                setLoading(false);
            }
        })();
    }, [token]);

    async function submit(e) {
        e.preventDefault();
        setError("");
        if (password.length < 8) {
            setError("Password must be at least 8 characters");
            return;
        }
        if (password !== confirm) {
            setError("Passwords do not match");
            return;
        }
        setSubmitting(true);
        try {
            await apiAcceptInvite(token, password);
            await refresh();
            toast.success("Welcome to NightOwl");
            navigate("/", { replace: true });
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div
            className="min-h-screen bg-ntt-surface flex flex-col"
            data-testid="accept-invite-page"
        >
            <header className="bg-[#070F26] text-white">
                <div className="max-w-6xl mx-auto px-6 h-14 flex items-center">
                    <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-[#0072BC] to-[#003a63] flex items-center justify-center">
                            <Moon className="h-4 w-4" />
                        </div>
                        <div className="text-[13px] font-semibold">NightOwl</div>
                    </div>
                </div>
            </header>

            <main className="flex-1 flex items-center justify-center px-4 py-16">
                <div className="w-full max-w-[480px] animate-fade-up">
                    <div className="bg-white border border-ntt-border rounded-xl p-8 shadow-[0_1px_2px_rgba(7,15,38,0.04),0_8px_24px_rgba(7,15,38,0.06)]">
                        {loading ? (
                            <div className="text-center py-8 text-sm text-ntt-muted">
                                Checking invite…
                            </div>
                        ) : loadError ? (
                            <div
                                data-testid="invite-load-error"
                                className="flex items-start gap-2 text-sm text-red-700"
                            >
                                <AlertCircle className="h-4 w-4 mt-[2px] shrink-0" />
                                <div>
                                    <div className="font-medium">
                                        This invite is not valid.
                                    </div>
                                    <div className="text-[13px] text-ntt-muted mt-1">
                                        {loadError}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="flex items-center gap-2 text-[12px] font-medium text-[#0072BC] mb-3">
                                    <CheckCircle2 className="h-4 w-4" />
                                    Invite verified
                                </div>
                                <h1
                                    className="text-2xl font-semibold text-[#070F26] mb-1"
                                    data-testid="invite-title"
                                >
                                    Set your password
                                </h1>
                                <p className="text-sm text-ntt-muted mb-6">
                                    Hello{" "}
                                    <span
                                        className="font-medium text-[#070F26]"
                                        data-testid="invite-name"
                                    >
                                        {invite.full_name}
                                    </span>
                                    , you have been invited to NightOwl as{" "}
                                    <span
                                        className="font-medium text-[#070F26]"
                                        data-testid="invite-role"
                                    >
                                        {invite.role}
                                    </span>
                                    . Signing in with{" "}
                                    <span
                                        className="font-medium text-[#070F26]"
                                        data-testid="invite-email"
                                    >
                                        {invite.email}
                                    </span>
                                    .
                                </p>

                                <form
                                    onSubmit={submit}
                                    className="space-y-4"
                                    data-testid="invite-form"
                                >
                                    <div>
                                        <Label htmlFor="inv-pw" className="text-[12px] text-ntt-muted mb-1.5 block">
                                            Choose a password
                                        </Label>
                                        <Input
                                            id="inv-pw"
                                            data-testid="invite-password-input"
                                            type="password"
                                            required
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            className="h-11"
                                            autoComplete="new-password"
                                        />
                                        <p className="text-[11px] text-ntt-muted mt-1">
                                            Minimum 8 characters.
                                        </p>
                                    </div>
                                    <div>
                                        <Label htmlFor="inv-cf" className="text-[12px] text-ntt-muted mb-1.5 block">
                                            Confirm password
                                        </Label>
                                        <Input
                                            id="inv-cf"
                                            data-testid="invite-confirm-input"
                                            type="password"
                                            required
                                            value={confirm}
                                            onChange={(e) => setConfirm(e.target.value)}
                                            className="h-11"
                                            autoComplete="new-password"
                                        />
                                    </div>
                                    {error && (
                                        <div
                                            data-testid="invite-error"
                                            className="flex items-start gap-2 text-[12px] text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2"
                                        >
                                            <AlertCircle className="h-4 w-4 mt-[1px] shrink-0" />
                                            <span>{error}</span>
                                        </div>
                                    )}
                                    <Button
                                        type="submit"
                                        disabled={submitting}
                                        data-testid="invite-submit-btn"
                                        className="w-full h-11 bg-[#0072BC] hover:bg-[#005a94] text-white font-medium"
                                    >
                                        {submitting
                                            ? "Setting up…"
                                            : "Set password and sign in"}
                                    </Button>
                                </form>
                            </>
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
}
